/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PLAYBACK_ERROR_OVERLAY_TIMEOUT_MS } from "../../config";
import { Locale } from "../../model/locale/Locale";
import { LocaleKeys } from "../../model/locale/LocaleKeys";
import { Log } from "../../model/logging";
import { ScreenControllerTypes } from "../../model/navigation";
import {
  ErrorSurface,
  resolveErrorSurface
} from "../../model/videoplayer/error";
import type { ErrorRoute } from "../../model/videoplayer/error";
import {
  hideOsd,
  selectIsOsdSuspended,
  selectIsOsdVisible,
  selectOsdEasCountry,
  showOsd
} from "../osd";
import { selectActiveScreen } from "../screens";
import {
  dismissUnityDialogWithoutAction,
  showUnityDialog
} from "../unityDialog";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import { exitToPreviousScreenRequested } from "../videoPlayerUi/videoPlayerUiSlice";
import type { DisplayErrorParams } from "./errorDisplay.types";

const TAG: string = "errorDisplayThunks";

/**
 * A dialog suppressed by R3 gets one replay when the OSD blocking it clears —
 * a single blocked/failed request can flip the connectivity OSD on and off
 * faster than the caller gets a second chance to show its dialog, which would
 * otherwise silently drop it for good. Cleared once replayed or superseded.
 */
let pendingDialogParams: DisplayErrorParams | null = null;

/**
 * displayError — the single entry point for putting an error on screen.
 *
 * Resolves the surface and priority from the error's code (see
 * `resolveErrorSurface`) and dispatches to the OSD or the full-screen dialog,
 * enforcing the cross-surface rules Kaveri implements across its OSD and
 * error-overlay reducers:
 *
 *  - **R1 / R2** — each surface's own priority guard (already in `osdSlice` /
 *    `unityDialogSlice`) decides whether the incoming error displaces what is
 *    already there.
 *  - **R3** — a visible OSD suppresses the dialog entirely, whatever the
 *    priorities. OSD codes live at 995–999 and dialog codes at 994, so this is
 *    consistent with the ladder rather than an exception to it; it is enforced
 *    here as well because the dialog is modal and would steal focus.
 *  - **R6** — dialogs open only while full-screen playback is the active screen
 *    controller. An error raised by a background player must not throw a modal
 *    over an unrelated screen. OSDs are not gated: they are non-blocking, and
 *    their producers scope them by subscribing/unsubscribing per screen.
 *  - **R7** — the dialog auto-hides and leaves the player.
 *
 * Callers never dispatch `showOsd` / `showUnityDialog` for errors directly.
 *
 * @param params - Code, already-localised text, and optional surface/priority
 *   overrides.
 */
export const displayError =
  (params: DisplayErrorParams) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const route: ErrorRoute = resolveErrorSurface(params.code);
    const surface: ErrorSurface =
      params.surface !== undefined ? params.surface : route.surface;
    const priority: number =
      params.priority !== undefined ? params.priority : route.priority;

    if (surface === ErrorSurface.OSD) {
      Log.i(
        TAG,
        "displayError() OSD code=" +
          params.code +
          " priority=" +
          String(priority)
      );
      dispatch(
        showOsd({
          message: params.message,
          code: params.code,
          priority: priority,
          // Errors are anchored — only long-lived idle prompts float to avoid
          // burn-in.
          isRepositionRequired: false
        })
      );
      return;
    }

    const state: UnityRootState = getState();

    // R3 — an OSD on screen blocks the modal outright.
    if (selectIsOsdVisible(state) === true) {
      Log.i(
        TAG,
        "displayError() dialog suppressed by visible OSD, code=" + params.code
      );
      pendingDialogParams = params;
      return;
    }

    // R6 — never throw a playback error modal over another screen.
    //
    // Gated on `activeScreen`, not `screenController`: the player is registered
    // via `setActiveScreen(FULL_SCREEN_PLAYBACK)` and `/player` has no entry in
    // `urlToScreenName`, so `screenController` is empty while playing. Same
    // basis `channelNumberEntrySlice` uses to detect the player.
    if (
      selectActiveScreen(state) !== ScreenControllerTypes.FULL_SCREEN_PLAYBACK
    ) {
      Log.i(
        TAG,
        "displayError() dialog suppressed off-player, code=" + params.code
      );
      return;
    }

    Log.i(
      TAG,
      "displayError() DIALOG code=" +
        params.code +
        " priority=" +
        String(priority)
    );
    pendingDialogParams = null;
    dispatch(
      showUnityDialog({
        title: params.title,
        message: params.message,
        variant: "error",
        errorCode: params.code,
        priority: priority,
        autoHide: true,
        autoHideTimeoutMs: PLAYBACK_ERROR_OVERLAY_TIMEOUT_MS,
        // R7 — an unacknowledged error dialog must not strand the user on a
        // player that is not playing anything.
        onAutoHide: (): void => {
          Log.i(TAG, "error dialog auto-hid — exiting player");
          dispatch(exitToPreviousScreenRequested());
        },
        onBackPress: params.onBack,
        // Producer-supplied primary/secondary actions, or the default single
        // dismiss button when it doesn't need more than an acknowledgement.
        actions:
          params.actions !== undefined
            ? params.actions
            : [
                {
                  id: "ok",
                  label: Locale.getString(LocaleKeys.ALERT_ACTION_OK)
                }
              ]
      })
    );
  };

/**
 * dismissError — clear any error currently shown on the OSD.
 *
 * A full dismissal: `priority` resets to 0, so the next error of any priority
 * is free to show. Producers call this on the transitions that make an error
 * obsolete — restriction cleared, network restored, content loaded, leaving the
 * screen.
 *
 * Deliberately does NOT close the dialog: a modal error is acknowledged by the
 * user (or by its own auto-hide), not silently retracted underneath them.
 *
 * An EAS alert is never dismissed by this. `hideOsd()` resets the slice
 * outright, with none of the priority arbitration `showOsd()` applies, so
 * without this guard any producer clearing its own obsolete error — the
 * `dismissError()` on leaving full-screen playback, a restriction lifting, the
 * network coming back — would silently wipe an emergency alert that is meant
 * to outrank everything on screen. EAS is cleared only by its own lifecycle,
 * through `EamBridge`.
 */
export const dismissError =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const state: UnityRootState = getState();

    // Both halves are required. `hideOsd()` clears `isVisible` but deliberately
    // leaves `message`, `code` and `easCountry` in place so exit animations can
    // still read them, so `easCountry` alone stays truthy after an alert has
    // been dismissed and would suppress every ordinary dismissal until the next
    // `showOsd()` overwrote it. Suspension counts as on screen: `suspendOsd()`
    // hides the OSD while keeping it outstanding for `resumeOsd()` to bring
    // back, and an alert that is coming back must not be cleared underneath.
    const isEasAlertOnScreen: boolean =
      selectOsdEasCountry(state).length > 0 &&
      (selectIsOsdVisible(state) || selectIsOsdSuspended(state));

    if (isEasAlertOnScreen) {
      Log.i(TAG, "dismissError() — EAS alert on screen, OSD left untouched");
      dismissUnityDialogWithoutAction(dispatch);
      return;
    }
    dispatch(hideOsd());
    dismissUnityDialogWithoutAction(dispatch);

    // Give a dialog stashed by displayError()'s R3 suppression its replay
    // now that the OSD blocking it is gone.
    if (pendingDialogParams !== null) {
      const retryParams: DisplayErrorParams = pendingDialogParams;
      pendingDialogParams = null;
      dispatch(displayError(retryParams));
    }
  };
